/**
 * Firebase Configuration with Failover Support
 *
 * The active Firebase app (primary | secondary) is now chosen ONCE at bootstrap
 * in `firebaseRuntime.ts` (see that file for the selection rules). This module
 * re-exports those active instances and layers the runtime failover-aware
 * getters on top (used by `failoverService` for the control-doc-driven hot path).
 *
 * Cutover model (Phase 4): the primary mechanism is the static `runtime-config`
 * selector consumed by `firebaseRuntime.ts` (edit + reload). The control-doc
 * listener in `failoverService` remains the runtime source of truth and only
 * acts when a complete secondary config is present.
 */

import app, {
  storage,
  auth,
  db,
  functions,
  secondaryConfigIsComplete,
} from './firebaseRuntime';
import failoverService from '../services/failoverService';
import Logger from '../services/logger';

// Failover-aware service getters
export const getFailoverAwareDatabase = () => {
  const failoverDb = failoverService.getActiveDatabase();
  return failoverDb || db; // Fallback to active-bootstrap instance if hot-switch unavailable
};

export const getFailoverAwareAuth = () => {
  const failoverAuth = failoverService.getActiveAuth();
  return failoverAuth || auth;
};

export const getFailoverAwareStorage = () => {
  const failoverStorage = failoverService.getActiveStorage();
  return failoverStorage || storage;
};

// Failover status getters
export const getFailoverStatus = () => {
  return {
    isActive: failoverService.isFailoverActive(),
    currentProject: failoverService.getCurrentProject(),
    state: failoverService.getFailoverState(),
  };
};

// Initialize failover service (non-blocking, log-only).
const initializeFailover = async () => {
  try {
    if (secondaryConfigIsComplete) {
      Logger.info('Secondary Firebase configuration detected - failover enabled');
    } else {
      Logger.info('No secondary Firebase configuration - running in primary-only mode');
    }
    // failoverService auto-initializes on import and handles missing secondary config gracefully.
  } catch (error) {
    Logger.error(
      'Failover initialization failed',
      error instanceof Error ? error : new Error('Unknown error')
    );
  }
};

initializeFailover();

// Export existing services for backward compatibility
export { app as default, storage, auth, db, functions };

// Export failover-aware services
export {
  getFailoverAwareDatabase as getDatabase,
  getFailoverAwareAuth as getAuth,
  getFailoverAwareStorage as getStorage,
};
